import { useEffect } from "react";
import type { Virtualizer } from "@tanstack/react-virtual";

type Options = {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
};

/**
 * Fetches the next infinite-query page once the virtualizer renders the last item
 * (overscan provides the prefetch lead). Keeps fetching until the viewport is filled.
 */
export function useVirtualizerLoadMore<TScrollElement extends Element, TItemElement extends Element>(
  virtualizer: Virtualizer<TScrollElement, TItemElement>,
  { hasNextPage, isFetchingNextPage, fetchNextPage }: Options,
): void {
  const virtualItems = virtualizer.getVirtualItems();
  const lastIndex = virtualItems[virtualItems.length - 1]?.index ?? -1;
  const count = virtualizer.options.count;

  useEffect(() => {
    if (lastIndex < 0 || !hasNextPage || isFetchingNextPage) return;
    if (lastIndex >= count - 1) fetchNextPage();
  }, [lastIndex, count, hasNextPage, isFetchingNextPage, fetchNextPage]);
}
